import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const usd = (n) => n.toLocaleString(undefined, { style: "currency", currency: "USD" });

function Stat({ label, value, sub, testId, dark }) {
  return (
    <div className={`rounded-2xl border p-5 ${dark ? "border-[#2C1810] bg-[#2C1810] text-[#FDFBF7]" : "border-[#E6DBCF] bg-white/70"}`} data-testid={testId}>
      <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${dark ? "text-[#C5A059]" : "text-[#A88B7D]"}`}>{label}</p>
      <p className={`mt-2 font-serif text-4xl font-semibold ${dark ? "" : "text-[#2C1810]"}`} data-testid={`${testId}-value`}>{value}</p>
      {sub && <p className={`mt-1 text-xs ${dark ? "text-[#EADFD2]" : "text-[#6E5549]"}`}>{sub}</p>}
    </div>
  );
}

export function RevenuePanel() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({ queryKey: ["admin-revenue"], queryFn: async () => (await api.get("/admin/revenue")).data });
  if (isLoading) return <p className="text-sm text-[#6E5549]">Loading revenue…</p>;
  if (isError) return <p role="alert" className="text-sm text-[#8C2A2A]">Couldn't load revenue. <button className="underline" onClick={() => refetch()}>Retry</button></p>;
  const c = data.counts;
  return (
    <div className="space-y-6" data-testid="admin-revenue">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#6E5549]">{data.total_artists} artist accounts · amounts in USD from Stripe</p>
        <button onClick={() => refetch()} disabled={isFetching} data-testid="admin-revenue-refresh" className="inline-flex min-h-[40px] items-center gap-2 rounded-full border border-[#E6DBCF] px-4 text-xs font-semibold text-[#2C1810] hover:bg-[#F6F1EA] disabled:opacity-50">
          <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Stat dark label="Monthly recurring revenue" value={usd(data.mrr)} sub={`${data.plans.monthly} monthly · ${data.plans.yearly} yearly (counted as ÷ 12)`} testId="revenue-mrr" />
        <Stat dark label={`Collected in ${data.month}`} value={usd(data.collected_this_month)} sub={data.stripe_error || `${data.paid_invoices_this_month} paid invoice${data.paid_invoices_this_month === 1 ? "" : "s"}`} testId="revenue-collected" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Paying" value={c.active} sub={c.canceling ? `${c.canceling} set to cancel at period end` : "Active memberships"} testId="revenue-paying" />
        <Stat label="In free trial" value={c.trialing} sub="Card on file, first charge when the trial ends" testId="revenue-trialing" />
        <Stat label="Payment issue" value={c.past_due} sub="Past due, Stripe is retrying" testId="revenue-past-due" />
        <Stat label="Cancelled" value={c.canceled} sub="Membership ended" testId="revenue-canceled" />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="PMU Accelerator partners" value={c.partner ?? 0} sub="Complimentary, not billed" testId="revenue-partner" />
        <Stat label="Launch access (no card yet)" value={c.launch_grace} sub="Existing artists in their free launch window" testId="revenue-launch" />
        <Stat label="No membership" value={c.no_membership} sub="Signed up but never started a trial" testId="revenue-none" />
      </div>
    </div>
  );
}
